import { Autocomplete, Button, createFilterOptions } from '@mui/material';
import { AppSimpleSelect, AppTextInput } from 'components/FormElements';
import Spacing from 'components/Spacing';
import { styled } from '@mui/material';
import { useEffect, useState } from 'react';
import { FieldRow } from 'components/FormElements';
import { useMediaQuery } from '@mui/material';
import { CategoryOptions, TagData } from './types';

const InputWrapper = styled('div')({
  flex: '1 1 50%',
  '@media (max-width: 600px)': {
    flex: '1 1 100%',
  },
  '& .MuiInputBase-root': {
    width: '100%',
  },
});

const SubmitButton = styled(Button)({
  minWidth: '90px',
});

export type CustomTagInput = {
  inputValue?: string;
  tag: string;
};

type Props = {
  onTagSelect: (customTag: TagData) => void;
  onCategorySelect: (category: string) => void;
  showButton?: boolean;
  tagsHiddenFromOptions?: Array<{
    category: string;
    tag: string;
  }>;
  showLabels?: boolean;
  selectedCategory?: string;
  categoryOptions: CategoryOptions;
  tagOptions?: Array<string>;
};

const Selector: React.FC<Props> = ({
  onTagSelect,
  onCategorySelect,
  showButton = false,
  tagsHiddenFromOptions,
  showLabels = false,
  categoryOptions,
  selectedCategory,
  tagOptions,
}: Props) => {
  const [allowEnterToConfirmNewTag, setAllowEnterToConfirmNewTag] =
    useState(false);

  const filter = createFilterOptions<CustomTagInput>();
  const [customTagValue, setCustomTagValue] = useState<CustomTagInput | null>(
    null,
  );

  const hiddenTagsInCategory = tagsHiddenFromOptions
    ?.filter((hiddenTag) => hiddenTag.category === selectedCategory)
    .map((tag) => tag.tag);

  const availableCustomTags = [...(tagOptions ?? [])]
    .filter((tag) => !hiddenTagsInCategory?.includes(tag))
    .map((tag) => ({ tag }));

  const handleSelect = () => {
    if (selectedCategory && customTagValue) {
      onTagSelect({ category: selectedCategory, tag: customTagValue.tag });
      setCustomTagValue(null);
    }
  };

  useEffect(() => {
    if (!showButton) {
      handleSelect();
    }
  }, [customTagValue, selectedCategory, showButton]);

  const isMobile = useMediaQuery('(max-width: 600px)');

  return (
    <Spacing size="md" direction={isMobile ? 'column' : 'row'} fullWidth>
      <InputWrapper>
        <FieldRow title={showLabels || isMobile ? 'Category' : ''} fullWidth>
          <AppSimpleSelect
            options={categoryOptions}
            onChange={(event) => {
              if (!event.target.value) return;
              onCategorySelect(event.target.value as string);
            }}
            value={selectedCategory}
            onOpen={() => {
              setCustomTagValue(null);
            }}
            placeholder="Select a category"
          />
        </FieldRow>
      </InputWrapper>
      <InputWrapper>
        <FieldRow title={showLabels || isMobile ? 'Tag' : ''} fullWidth>
          <Autocomplete<CustomTagInput>
            disabled={!selectedCategory}
            clearOnBlur
            handleHomeEndKeys
            selectOnFocus
            options={availableCustomTags}
            value={customTagValue}
            onChange={(_, newValue: CustomTagInput | null | string) => {
              if (typeof newValue === 'string') {
                setCustomTagValue({ tag: newValue });
              } else if (newValue?.inputValue) {
                setCustomTagValue({ tag: newValue.inputValue });
              } else {
                setCustomTagValue(newValue);
              }
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                if (allowEnterToConfirmNewTag) {
                  // Get the current input value
                  const inputValue = event.currentTarget
                    .querySelector('input')
                    ?.value.trim();
                  if (inputValue && selectedCategory) {
                    setCustomTagValue({ tag: inputValue });
                    setAllowEnterToConfirmNewTag(false);
                  }
                }
              }
            }}
            renderInput={(params) => (
              <AppTextInput {...params} placeholder="Enter or select a tag" />
            )}
            filterOptions={(options, params) => {
              const filtered = filter(options, params);
              const { inputValue } = params;
              const trimmedInputValue = inputValue.trim();

              // Suggest the creation of a new value
              if (trimmedInputValue !== '') {
                const tagExistsInCategory =
                  tagOptions?.includes(trimmedInputValue) ||
                  hiddenTagsInCategory?.includes(trimmedInputValue);

                setAllowEnterToConfirmNewTag(!tagExistsInCategory);

                if (!tagExistsInCategory) {
                  filtered.push({
                    inputValue: trimmedInputValue,
                    tag: `Add "${trimmedInputValue}" as a new tag`,
                  });
                }
              } else {
                setAllowEnterToConfirmNewTag(false);
              }

              return filtered;
            }}
            getOptionLabel={(option: CustomTagInput | string) => {
              if (typeof option === 'string') {
                return option;
              }
              if (option.inputValue) {
                return option.inputValue;
              }
              return option.tag;
            }}
            getOptionKey={(option: CustomTagInput | string) =>
              typeof option === 'string'
                ? option
                : option.inputValue
                ? option.inputValue
                : option.tag
            }
            renderOption={(props, option: CustomTagInput) => (
              <li {...props}>{option.tag}</li>
            )}
          />
        </FieldRow>
      </InputWrapper>
      {showButton && (
        <SubmitButton
          variant="contained"
          color="primary"
          onClick={handleSelect}
        >
          Add tag
        </SubmitButton>
      )}
    </Spacing>
  );
};

export default Selector;
